import React, {useState, useRef, useEffect} from 'react';
import {theme} from '@styles/theme';
import Text from '@atoms/Text';
import {ChevronSort, Close, Search} from '@carbon/icons-react';
import {
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  TableRow,
  HeaderContent,
  SortIcon,
} from './ModulesTable.styles';
import {
  ClearButton,
  HeaderTextWithIcon,
  SearchInputContainer,
  StyledSearchInput,
  TableCell,
  TableHeaderCell,
} from './TestsTable.styles';
import Checkbox from '@atoms/Checkbox';
import CircleButton from '@atoms/CircleButton';
import {ITest} from '@types';
import {IDLE, PENDING} from '@constants/loadingState';
import Skeleton from 'react-loading-skeleton';
import {formatLastUpdate} from '@utils/common';

interface TestsTableProps {
  tests: ITest[];
  onSort: (field: string) => void;
  onSelectRow?: (rowsIds: number[]) => void;
  onDelete: (test_id: number) => void;
  onEdit: (test_id: number) => void;
  loading: string;
}

const TestsTable: React.FC<TestsTableProps> = ({
  tests,
  onSort,
  onSelectRow,
  onDelete,
  onEdit,
  loading,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTests, setSelectedTests] = useState<number[]>([]);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const filteredTests = tests.filter(test =>
    test.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    if (onSelectRow) onSelectRow(selectedTests);
  }, [selectedTests]);

  const allSelected = tests.length > 0 && selectedTests.length === tests.length;

  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      const allIds = tests.map(test => test.id);
      setSelectedTests(allIds);
    } else {
      setSelectedTests([]);
    }
  };

  const handleSelectTest = (testId: number, e: any) => {
    if (e.target.checked) {
      setSelectedTests(prev => [...prev, testId]);
    } else {
      setSelectedTests(prev => prev.filter(id => id !== testId));
    }
  };

  // Обработчик клика по ячейке названия теста
  const handleHeaderClick = () => {
    setShowSearchInput(true);
  };

  // Очистка поиска и скрытие инпута
  const handleClearSearch = () => {
    setSearchTerm('');
    setShowSearchInput(false);
  };

  // Обработчик клика вне инпута
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node) &&
        searchTerm === ''
      ) {
        setShowSearchInput(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchTerm]);

  // Фокус на инпут при его появлении
  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            {/* Checkbox column */}
            <TableHeaderCell>
              <Checkbox
                id={'select-all-tests'}
                checked={allSelected}
                onChange={handleSelectAll}
              />
            </TableHeaderCell>

            {/* Test Name column */}
            <TableHeaderCell onClick={handleHeaderClick} style={{cursor: 'pointer'}}>
              {showSearchInput ? (
                <SearchInputContainer ref={searchContainerRef}>
                  <StyledSearchInput
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <ClearButton onClick={handleClearSearch}>
                      <Close size={12} />
                    </ClearButton>
                  )}
                </SearchInputContainer>
              ) : (
                <HeaderContent>
                  <HeaderTextWithIcon>
                    <Search size={16} />
                    <Text text="Название теста" type="tinyM" />
                  </HeaderTextWithIcon>
                  <SortIcon onClick={() => onSort('name')}>
                    <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                  </SortIcon>
                </HeaderContent>
              )}
            </TableHeaderCell>

            {/* Integrations/Modules count column */}
            <TableHeaderCell>
              <HeaderContent>
                <Text text="Кол-во интеграций/модулей" type="tinyM" />
                <SortIcon onClick={() => onSort('count')}>
                  <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                </SortIcon>
              </HeaderContent>
            </TableHeaderCell>

            {/* Automated column */}
            <TableHeaderCell>
              <HeaderContent>
                <Text text="Автоматизировано" type="tinyM" />
                <SortIcon onClick={() => onSort('automated')}>
                  <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                </SortIcon>
              </HeaderContent>
            </TableHeaderCell>

            {/* Last Activity column */}
            <TableHeaderCell>
              <HeaderContent>
                <Text text="Последняя активность" type="tinyM" />
                <SortIcon onClick={() => onSort('lastActivity')}>
                  <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                </SortIcon>
              </HeaderContent>
            </TableHeaderCell>

            {/* Actions column (empty header) */}
            <TableHeaderCell>{/* Empty header for actions */}</TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading === IDLE &&
            filteredTests.map(test => (
              <TableRow key={test.id}>
                {/* Checkbox cell */}
                <TableCell>
                  <Checkbox
                    id={`select-test-id-${test.id}`}
                    checked={selectedTests.includes(test.id)}
                    onChange={selected => handleSelectTest(test.id, selected)}
                  />
                </TableCell>

                {/* Test Name cell */}
                <TableCell>
                  <Text text={test.name} type="tinyR" />
                </TableCell>

                {/* Integrations/Modules count cell */}
                <TableCell>
                  <Text text={`${test.modules_count || 0}`} type="tinyR" />
                </TableCell>

                {/* Automated cell */}
                <TableCell>
                  <Text
                    text={`${test.auto_coverage ? test.auto_coverage.toFixed(2) : '0.00'}%`}
                    type="tinyNum"
                  />
                </TableCell>

                {/* Last Activity cell */}
                <TableCell>
                  <Text text={formatLastUpdate(test.last_active || '')} type="tinyR" />
                </TableCell>

                {/* Actions cell */}
                <TableCell>
                  <CircleButton
                    key={`table-test-menu-${test.id}`}
                    menu={[
                      {title: 'Редактировать', onClick: () => onEdit(test.id)},
                      {
                        title: 'Удалить',
                        onClick: () => onDelete(test.id),
                        color: theme.colors.signalRed00,
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          {loading === PENDING &&
            [...Array(4)].map(i => (
              <TableRow key={`test-table-row-${i}`}>
                <TableCell>
                  <Skeleton width={18} height={18} />
                </TableCell>
                <TableCell>
                  <Skeleton />
                </TableCell>
                <TableCell>
                  <Skeleton />
                </TableCell>
                <TableCell>
                  <Skeleton />
                </TableCell>
                <TableCell>
                  <Skeleton />
                </TableCell>
                <TableCell>
                  <Skeleton circle width={32} height={32} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TestsTable;
